/**
 * The live consent decision, shared between the banner and the privacy page.
 *
 * An external store rather than a React context: two unrelated places in the tree read
 * and write this, and a context would mean a provider wrapping the whole app to carry
 * one nullable string. `useSyncExternalStore` gives the same reactivity without it.
 */

import { applyConsent, initAnalytics } from './analytics';
import { clearConsent, readConsent, storeConsent, type ConsentChoice } from './consent';

type Listener = () => void;

const listeners = new Set<Listener>();

// Read lazily rather than at import time. `null` is a meaningful value here - it means
// "not yet decided" - so a separate flag, not a nullish check, tracks whether the
// stored value has been loaded.
let loaded = false;
let snapshot: ConsentChoice | null = null;

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The current decision, or `null` if the visitor has not made one.
 *
 * Must return a cached value: `useSyncExternalStore` re-renders whenever the snapshot
 * differs by identity, so building a fresh object here would loop.
 */
export function getConsentSnapshot(): ConsentChoice | null {
  if (!loaded) {
    snapshot = readConsent();
    loaded = true;
  }
  return snapshot;
}

/** Records a decision, applies it to the live tag, and notifies subscribers. */
export function setConsent(choice: ConsentChoice): void {
  storeConsent(choice);
  applyConsent(choice);
  snapshot = choice;
  loaded = true;
  emit();
}

/**
 * Forgets the decision so the banner asks again.
 *
 * Analytics is denied on the way out rather than left as it was: withdrawing an answer
 * has to withdraw what the answer permitted, or "ask me again" would quietly keep
 * collecting until the visitor answered a second time.
 */
export function resetConsent(): void {
  clearConsent();
  applyConsent('denied');
  snapshot = null;
  loaded = true;
  emit();
}

/**
 * Loads the tag with everything denied, then re-applies a stored decision if there is
 * one. Called once when the app mounts.
 */
export function bootstrapConsent(): void {
  initAnalytics();

  if (getConsentSnapshot() === 'granted') {
    applyConsent('granted');
  }
}
